// streamChat.workerClient.js
// For Webpack/CRA you’ll import differently — tell me your build tool if needed.

import { API_BASE } from '../config/vars';
import { getAuth0Client } from './auth0';
import { getBrowserLocalDateTime } from '../utils/dates';

/**
 * Open an SSE stream for AI chat using fetch-event-source.
 * Backend endpoint: POST /v1/ai/chat/:threadId
 * @param {string} threadId - Thread ID for conversation memory
 * @param {Object} payload - Payload object { message, resourceOverride?, additionalData? }
 * @param {Object} handlers - Event handlers { onCustomEvent, onError, onClose }
 * @param {Object} options - Options { timeoutMs }
 * @returns {Object} Object with cancel() function
 */

const worker  = new Worker(
  // $FlowFixMe[prop-missing]
  new URL('./aiChatStreamWorker.js', import.meta.url),
  { type: 'module' }
);

let nextId = 1;

// streamId -> callbacks
const handlersById = new Map();

worker.onmessage = (e) => {
  const msg = e.data;
  if (!msg || !msg.type) return;

  const handlers = handlersById.get(msg.streamId);
  if (!handlers) return;

  if (msg.type === "MESSAGE") {
    const { eventName, data } = msg;

    // Mirror your current behavior
    if (!eventName) return;

    try {
      const payload = JSON.parse(data || "{}");

      if (eventName === "custom-event") {
        handlers.onCustomEvent?.(payload);
      } else if (eventName === "server-error") {
        const errorMessage = payload?.message ? String(payload.message) : "An error occurred";
        handlers.onError?.(errorMessage);
        handlers.cleanup?.();
      } else if (eventName === "close") {
        if (payload?.reason === "workflow-finished-error") {
          handlers.onError?.("An error occurred");
        }
        handlers.cleanup?.();
      }
    } catch {
      // ignore parse errors
    }
    return;
  }

  if (msg.type === "ERROR") {
    handlers.onError?.(msg.message || "Connection error");
    handlers.cleanup?.();
    return;
  }

  if (msg.type === "CLOSE") {
    handlers.cleanup?.(/* already closed */);
    return;
  }
};

export async function streamChat(threadId, payload, handlers = {}, options = {}) {
  const { timeoutMs } = options || {};
  const { onCustomEvent, onError, onClose } = handlers || {};

  // auth token stays on main thread (Auth0 client isn't available in worker)
  const client = getAuth0Client && getAuth0Client();
  const token = client && client.getTokenSilently ? await client.getTokenSilently() : "";

  const base = String(API_BASE || "").replace(/\/$/, "");
  const url = `${base}/v1/ai/chat/${encodeURIComponent(threadId)}`;

  const streamId = String(nextId++);
  let closed = false;

  const cleanup = () => {
    if (closed) return;
    closed = true;
    handlersById.delete(streamId);
    if (typeof onClose === "function") onClose();
  };

  handlersById.set(streamId, { onCustomEvent, onError, onClose, cleanup });

  worker.postMessage({
    type: "START",
    streamId,
    url,
    headers: {
      "Content-Type": "application/json",
      "Accept": "text/event-stream",
      "Cache-Control": "no-cache",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ...payload, synthetic: !!window.isDemoOrg, browserLocalDateTime: getBrowserLocalDateTime() }),
    timeoutMs,
  });

  return {
    cancel: () => {
      if (closed) return;
      worker.postMessage({ type: "CANCEL", streamId });
      cleanup();
    },
  };
}
